import type { HomePageCopy } from './homePage';

export const homePageEs: HomePageCopy = {
    hero: {
        badge: 'Inspeccionar · Mantener · Rastrear · Capacitar · Reportar',
        title: 'El sistema operativo para su flota de equipos',
        description:
            'Inspecciones, mantenimiento preventivo, control de llantas, reportes de costos y capacitación en seguridad. Una sola plataforma, pensada para operaciones de trituración, cuadrillas de construcción y obras con mucho equipo.',
        startFreeDemo: 'Iniciar demo gratis',
        contactUs: 'Contáctenos',
        heroImageAlt: 'Plataforma TotalEquipIQ en escritorio y móvil',
    },
    trust: {
        title: 'Hecho para quienes realmente operan el equipo',
        subtitle:
            'Supervisores, operadores, mecánicos y gerentes trabajan en el mismo sistema. Se acabaron las hojas de cálculo, el papeleo perdido y las dudas sobre qué se ha servido.',
        cards: [
            {
                title: 'Hecho para el campo',
                body: 'Diseñado para uso móvil en entornos exigentes. Guantes, sol, polvo e inspecciones rápidas incluidos.',
            },
            {
                title: 'Responsabilidad clara',
                body: 'Cada inspección, servicio y gasto se vincula a un usuario, un momento y un activo. Nada se queda sin seguimiento.',
            },
            {
                title: 'Fácil de adoptar',
                body: 'Flujos que sus cuadrillas ya conocen. Capacitación mínima, despliegue rápido y resultados reales en semanas.',
            },
        ],
    },
    problems: {
        imageAlt: 'Gestión de equipos en campo',
        title: 'La gestión de equipos no debería vivir en hojas de cálculo y libretas',
        cards: [
            {
                icon: '📄',
                title: 'El papel se pierde',
                body: 'Inspecciones, registros de MP y gastos desaparecen antes de que alguien actúe.',
            },
            {
                icon: '⏰',
                title: 'Problemas detectados demasiado tarde',
                body: 'Cuando la persona correcta se entera del problema, ya es una falla.',
            },
            {
                icon: '📊',
                title: 'Los datos están por todos lados',
                body: 'La info del equipo está en una hoja, el MP en una libreta y las inspecciones no se comunican entre sí.',
            },
            {
                icon: '💰',
                title: 'Sin una imagen real de costos',
                body: 'No puede ver qué activos son rentables y cuáles drenan dinero en reparaciones.',
            },
        ],
    },
    connected: {
        title: 'Todo conectado, nada aislado',
        subtitle:
            'Cuando inspecciones, mantenimiento, costos y capacitación comparten los mismos datos, su flota deja de pelearle.',
        cards: [
            {
                title: 'Una plataforma, cada flujo',
                body: 'Inspecciones, MP, llantas, costos y capacitación viven en un solo sistema. Se acabó conciliar hojas de cálculo a fin de mes.',
            },
            {
                title: 'Reportes en tiempo real',
                body: 'Los tableros se actualizan al ritmo de su cuadrilla. Vea qué vence, qué está atrasado y cuánto cuesta cada activo sin esperar a que alguien lo arme.',
            },
            {
                title: 'Problemas marcados automáticamente',
                body: 'Lo detectado en una inspección crea problemas, se asigna a la persona correcta y se vincula al equipo que necesita atención.',
            },
            {
                title: 'Rastro de auditoría permanente',
                body: 'Cada inspección, servicio, gasto y registro de capacitación se vincula a un usuario, una marca de tiempo y un activo. Listo para auditoría por defecto.',
            },
        ],
    },
    modules: {
        sectionTitle: 'Hecho para quienes realmente operan el equipo.',
        sectionSubtitle: 'Comience con la plataforma central. Agregue los módulos que necesita y omita los que no.',
        coreHeader: {
            highlight: 'Módulos',
            rest: ' centrales',
            subtitle: 'Todo lo siguiente está incluido en cada plan.',
        },
        learnMore: 'Más información →',
        core: [
            {
                imageAlt: 'Interfaz de inspecciones',
                label: 'Escanear. Inspeccionar. Listo.',
                title: 'Inspecciones + QR',
                body: 'Imprima un código QR, péguelo en la máquina y escanéelo con el teléfono. Se abre el formulario correcto para el activo correcto.',
                bullets: [
                    'Funciona con guantes y bajo sol intenso',
                    'Los ítems marcados crean problemas automáticamente',
                    'El inspector firma y envía en menos de 2 minutos',
                ],
            },
            {
                imageAlt: 'Gestión de equipos',
                label: 'Una sola fuente de verdad para sus activos y su equipo.',
                title: 'Equipos y usuarios',
                body: 'Cada equipo tiene un perfil. Cada miembro del equipo tiene un registro. Inspecciones, servicios y capacitación se vinculan a ambos.',
                bullets: [
                    'Organice equipos por categoría, sitio o estado',
                    'Rastree UM, utilización y ciclo de vida por activo',
                    'Administre operadores, mecánicos y administradores desde una sola pantalla',
                ],
            },
            {
                imageAlt: 'Operaciones diarias',
                label: 'Plataforma central, completa.',
                title: 'El día a día, resuelto.',
                body: 'Briefing diario, reportes, problemas, formularios, recursos y más, todo en la plataforma central. Sin complementos ni ventas adicionales para lo básico que su operación necesita cada día.',
                bullets: [
                    'Briefing de seguridad diario al iniciar sesión',
                    'Seguimiento de problemas desde la inspección hasta la resolución',
                    'Reportes de equipos, usuarios y actividad',
                ],
            },
        ],
        addonHeader: {
            highlight: 'Módulos',
            rest: ' adicionales',
            subtitle: 'Elija los que necesita. Omita los que no.',
        },
        addons: [
            {
                imageAlt: 'Mantenimiento preventivo',
                label: 'Deje de perseguir los MP en una libreta.',
                title: 'Mantenimiento preventivo',
                body: 'Defina niveles de servicio, partes y procedimientos, y deje que el sistema le diga qué vence.',
                bullets: [
                    'Intervalos personalizados por tipo de equipo',
                    'Partes, proveedores y tarifas de mano de obra integrados',
                    'Colas de Próximo a vencer, Atrasado y Programado de un vistazo',
                ],
            },
            {
                imageAlt: 'Gestión de llantas',
                label: 'Rastree cada llanta desde la instalación hasta el reemplazo.',
                title: 'Gestión de llantas',
                body: 'Cree plantillas de ejes, asigne llantas a posiciones y registre la profundidad del dibujo en una sola pantalla.',
                bullets: [
                    'Plantillas de ejes reutilizables (4x2, 6x4, 12x4, personalizadas)',
                    'Diagramas en vivo conforme los inspectores registran lecturas',
                    'Alertas de reemplazo por porcentaje de dibujo',
                ],
            },
            {
                imageAlt: 'Control de costos',
                label: 'Sepa cuánto le cuesta realmente su equipo.',
                title: 'Control de costos',
                body: 'Registre cada gasto contra el activo que lo generó. MP, partes, mano de obra, llantas, reparaciones.',
                bullets: [
                    'Categorías de gastos con proveedor y adjunto de factura',
                    'Seguimiento de tarifas de mano de obra interna',
                    'Reportes de costo por UM por rango de fechas',
                ],
            },
            {
                imageAlt: 'Capacitación en seguridad',
                label: 'Capacitación lista para MSHA, programada día a día.',
                title: 'Seguridad y capacitación',
                body: 'Arme un plan de capacitación, coloque temas en un calendario de 365 días y el video correcto aparece el día correcto para cada usuario.',
                bullets: [
                    'Ventanas de capacitación diarias al iniciar sesión',
                    'Seguimiento de cumplimiento por categoría',
                    'Registros permanentes de capacitación y declaraciones juradas',
                ],
            },
        ],
        coreImages: [
            '/homepage-images/inspections-QR.png',
            '/homepage-images/equipment-users.png',
            '/homepage-images/day-to-day-handled.png',
        ],
        addonImages: [
            '/homepage-images/pm-image1.png',
            '/homepage-images/tire-module-1.png',
            '/homepage-images/cost-tracking.png',
            '/homepage-images/safety-training-1.png',
        ],
    },
    pricing: {
        title: 'Pague por lo que usa. Agregue lo que necesita.',
        body: 'Cada plan incluye inspecciones, equipos, usuarios y capacitación en seguridad lista para MSHA. Agregue mantenimiento preventivo, control de llantas o reportes de costos conforme crezca su operación. Actívelos en minutos, sin migraciones complicadas.',
        startFreeDemo: 'Iniciar demo gratis',
        contactQuote: 'Contáctenos para una cotización',
        imageAlt: 'Módulos de la plataforma central',
    },
    trial: {
        title: '30 días. Su equipo. Sus cuadrillas. Sin tarjeta de crédito.',
        body: 'Cree una cuenta en minutos. Active los módulos que quiera probar. Vea cómo lo usa su equipo antes de comprometerse.',
        cta: 'Iniciar demo gratis de 30 días',
        imageAlt: 'Cuadrilla de equipos',
    },
    roles: {
        title: 'Hecho para todos en la operación',
        cards: [
            {
                title: 'Operador / Cuadrilla de campo',
                bullets: [
                    'Escanear QR + inspeccionar + enviar rápido',
                    'Ver notas de la última inspección y problemas conocidos',
                    'Recibir temas diarios de seguridad al iniciar sesión',
                ],
            },
            {
                title: 'Mantenimiento / Mecánicos',
                bullets: [
                    'Ver qué MP vencen y qué partes se necesitan',
                    'Registrar inspecciones de llantas con diagramas en vivo',
                    'Rastrear reparaciones y mano de obra contra el activo correcto',
                ],
            },
            {
                title: 'Gerencia / Operaciones',
                bullets: [
                    'Preparación para auditorías en todos los sitios',
                    'Reportes de costo por UM por activo, categoría o rango de fechas',
                    'Seguimiento de cumplimiento MSHA, OSHA y estándares internos',
                ],
            },
            {
                title: 'Gerentes de seguridad',
                bullets: [
                    'Armar planes de capacitación con programación diaria de temas',
                    'Rastrear visualizaciones y generar declaraciones juradas',
                    'Detectar problemas recurrentes antes de que se vuelvan incidentes',
                ],
            },
        ],
    },
    testimonials: {
        label: 'Testimonios',
        title: 'Lo que dicen cuadrillas y supervisores',
        ratingsLabel: 'Calificación 5.0',
        authorPlaceholder: 'Nombre del autor',
        rolePlaceholder: 'Rol',
        prevAria: 'Anterior',
        nextAria: 'Siguiente',
        quotes: [
            '"Dejamos de perder inspecciones en el papeleo. Ahora todo es visible."',
            '"Escanear el QR lo hace simple: nadie tiene que \'buscar el formulario correcto.\'"',
        ],
    },
    faq: {
        label: 'Preguntas frecuentes',
        title: 'Respuestas directas para operadores y gerentes',
        items: [
            {
                question: '¿Cuánto tarda la configuración?',
                answer:
                    'La mayoría de las empresas ya hacen inspecciones en un día. El despliegue completo de la plataforma (calendarios de MP, inventario de llantas, categorías de costos) suele tomar de una a dos semanas según el tamaño de la flota.',
            },
            {
                question: '¿Cuánto dura la demo gratis?',
                answer:
                    '30 días, con acceso completo a la plataforma central. No se requiere tarjeta de crédito. También puede activar módulos adicionales durante la prueba para ver si encajan en su operación.',
            },
            {
                question: '¿Podemos empezar solo con lo central y agregar módulos después?',
                answer:
                    'Sí. Cada cuenta comienza con la plataforma central. Los módulos adicionales se pueden activar o desactivar en cualquier momento desde el panel de administración.',
            },
            {
                question: '¿Podemos crear formularios para distintos tipos de equipo?',
                answer:
                    'Sí. Cree formularios de inspección y servicio diario por categoría de equipo y asígnelos a activos específicos para que las cuadrillas siempre usen la lista correcta.',
            },
            {
                question: '¿Funciona sin conexión?',
                answer:
                    'Las inspecciones se pueden completar sin conexión y se sincronizan cuando el dispositivo se reconecta. Diseñado para pozos remotos, canteras y obras con cobertura irregular.',
            },
            {
                question: '¿Qué hay de la conformidad MSHA?',
                answer:
                    'El módulo de Seguridad y capacitación está incluido en lo central y se basa en las normas MSHA partes 46 y 48. Programación diaria de temas, declaraciones juradas y registros permanentes de capacitación son estándar.',
            },
            {
                question: '¿Podemos adjuntar fotos, notas o videos?',
                answer:
                    'Sí. Cada ítem de inspección admite fotos, notas y audio opcional. Los archivos se adjuntan al registro permanente del activo.',
            },
            {
                question: '¿Cómo funciona el seguimiento de problemas?',
                answer:
                    'Los ítems marcados en cualquier inspección crean automáticamente un problema vinculado al activo, al inspector y a la marca de tiempo. Los problemas van al módulo de Problemas y se rastrean hasta resolverse.',
            },
            {
                question: '¿Cómo funciona el precio?',
                answer:
                    'Plataforma central más módulos adicionales. Prueba gratis de 30 días, sin tarjeta de crédito. Contáctenos para una cotización según el tamaño de su flota.',
            },
        ],
    },
    finalCta: {
        title: '¿Listo para operar su flota en un solo sistema?',
        body: 'Vea cómo TotalEquipIQ conecta inspecciones, mantenimiento, llantas, costos y capacitación. Pruebe la plataforma completa gratis por 30 días, con su equipo real y sus cuadrillas reales.',
        startFreeDemo: 'Iniciar demo gratis',
        contactUs: 'Contáctenos',
        imageAlt: 'Cuadrilla de equipos en el trabajo',
    },
};
